// thead components for both Table modes — default (Consumed | Adherence) and
// detailed (Target | Consumed | Adherence).
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { MACROS } from "../../../../../utils/nutrition-progress";
import { HEAD_MACRO_COLORS, isColumnVisible, visibleTailKeys, TAIL_FIELD_CONFIG } from "./TableFormat";
import { translateMacroLabel, translateMacroUnit } from "./viewTokens";
import { activeColumnsPropType } from "./TableCells";

const statsKey = (key) => `nutritionPage.progress.tableTemplate.stats.${key}`;

// Row 1: empty group placeholders for the wearable/vitals tail — unlike
// Consumed/Adherence (default) or Target/Consumed/Adherence (detailed), Wt/
// Steps/Sleep/Day/HRV/RHR/Readiness/RPE/Water/Body fat carry no group label.
// Same visible set as TailHeadCells below, just empty cells.
const TailHeadGroup = ({ activeColumns }) => (
  <>
    {visibleTailKeys(activeColumns).map((key) => (
      <th key={key} className="np-table-head-cell" />
    ))}
  </>
);

// Row 2: Wt/Steps/Sleep/HRV/RHR/Readiness/RPE/Water/Body fat/Day labels —
// identical between default and detailed mode. Day is last. Every key but
// "days" reads its label/unit from TAIL_FIELD_CONFIG (TableFormat.js) — the
// same descriptor TableCells.js's DayTail/AvgTail use for the cell values,
// so a new tail stat is one config entry instead of a render function here
// AND in TableCells.js. `label`/`unit` are always `(ctx) => string` — 2 of
// them read a coach preference (RPE's label follows rpe/rir; Bodyweight/
// Water's unit follows metric/imperial), the rest just read `ctx.t`.
const TailHeadCells = ({ wUnit, waterUnit, rpeLabel, activeColumns, t }) => {
  const ctx = { wUnit, waterUnit, rpeLabel, t };
  return visibleTailKeys(activeColumns).map((key) => {
    if (key === "days") {
      return (
        <th key={key} className="np-table-head-cell np-table-head-edge">
          {t("nutritionPage.progress.table.columns.day")}
        </th>
      );
    }
    const { label, unit } = TAIL_FIELD_CONFIG[key];
    return (
      <th key={key} className="np-table-head-cell np-table-head-edge">
        {label(ctx)}
        {unit && <div className="np-table-head-sub">({unit(ctx)})</div>}
      </th>
    );
  });
};

export const DefaultHead = ({ showMacroAdh, wUnit, waterUnit, rpeLabel, activeColumns }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const showConsumed = isColumnVisible(activeColumns, "consumed");
  const showAdherence = isColumnVisible(activeColumns, "adherence");
  return (
    <thead>
      <tr>
        <th className="np-table-head-cell np-table-head-cell--left" />
        {showConsumed && (
          <th colSpan={MACROS.length} className="np-table-head-group">
            <span className="np-table-gradient-text">
              {showMacroAdh
                ? t("nutritionPage.progress.table.groupConsumedAdherence")
                : t(statsKey("consumed"))}
            </span>
          </th>
        )}
        {showAdherence && (
          <th className="np-table-head-group">
            <span className="np-table-gradient-text">{t(statsKey("adherence"))}</span>
          </th>
        )}
        <TailHeadGroup activeColumns={activeColumns} />
      </tr>
      <tr className="np-table-head-row--sub">
        <th className="np-table-head-cell np-table-head-cell--left">
          {t("nutritionPage.progress.table.columns.date")}
        </th>
        {showConsumed && MACROS.map((m, i) => (
          <th key={m} className={i === 0 ? "np-table-head-cell np-table-head-edge" : "np-table-head-cell"} style={{ color: HEAD_MACRO_COLORS[m] }}>
            <div>{translateMacroLabel(t, m)}</div>
            {m !== "kcal" && (
              <div className="np-table-head-sub">({translateMacroUnit(t, m)})</div>
            )}
          </th>
        ))}
        {showAdherence && (
          <th className="np-table-head-cell np-table-head-edge">{t("common:units.kcalAbbr")}</th>
        )}
        <TailHeadCells wUnit={wUnit} waterUnit={waterUnit} rpeLabel={rpeLabel} activeColumns={activeColumns} t={t} />
      </tr>
    </thead>
  );
};

export const DetailedGroup = ({ title }) => (
  <th colSpan={MACROS.length} className="np-table-head-group">
    <span className="np-table-gradient-text">{title}</span>
  </th>
);

const detailedHeadCellClass = (i) => `np-table-head-cell${i === 0 ? " np-table-head-edge" : ""}`;

// group id -> both the activeColumns key AND the stats.* i18n suffix (Target/
// Consumed/Adherence group titles reuse the same "Customise Table" modal
// keys, see statsKey above — the group id happens to equal the key itself).
const DETAILED_GROUP_KEYS = { t: "target", c: "consumed", a: "adherence" };

// The Adherence group's sub-header unit differs by mode: "(g)"/none (same
// rule Target/Consumed already use — kcal gets no unit label) when showing
// the signed over/under delta, since that IS grams; "(%)" when showing the
// % instead — a value that was never grams in the first place, so reusing
// Target/Consumed's "(g)" there (the bug the leader flagged) would still be
// wrong even with the toggle in place. The other 2 groups never vary.
const unitSubLabel = (t, group, m, adherenceAsPercent) => {
  if (group === "a" && adherenceAsPercent) return "(%)";
  return m === "kcal" ? null : `(${translateMacroUnit(t, m)})`;
};

export const DetailedHead = ({ wUnit, waterUnit, rpeLabel, activeColumns, adherenceAsPercent }) => {
  const { t } = useTranslation("athlete-portal");
  const visibleGroups = ["t", "c", "a"].filter((g) =>
    isColumnVisible(activeColumns, DETAILED_GROUP_KEYS[g])
  );
  // "Adherence" only fits the % reading — with a signed delta on display,
  // the group is titled "Over/Under" instead, so the header never claims to
  // be showing an adherence score when it's actually showing a deviation.
  const groupTitle = (g) =>
    g === "a" && !adherenceAsPercent
      ? t("nutritionPage.progress.table.groupOverUnder")
      : t(statsKey(DETAILED_GROUP_KEYS[g]));
  return (
    <thead>
      <tr>
        <th className="np-table-head-cell np-table-head-cell--left" />
        {visibleGroups.map((g) => (
          <DetailedGroup key={g} title={groupTitle(g)} />
        ))}
        <TailHeadGroup activeColumns={activeColumns} />
      </tr>
      <tr className="np-table-head-row--sub">
        <th className="np-table-head-cell np-table-head-cell--left">
          {t("nutritionPage.progress.table.columns.date")}
        </th>
        {visibleGroups.map((group) =>
          MACROS.map((m, i) => {
            const sub = unitSubLabel(t, group, m, adherenceAsPercent);
            return (
              <th key={`${group}-${m}`} className={detailedHeadCellClass(i)} style={{ color: HEAD_MACRO_COLORS[m] }}>
                <div>{translateMacroLabel(t, m)}</div>
                {sub && <div className="np-table-head-sub">{sub}</div>}
              </th>
            );
          })
        )}
        <TailHeadCells wUnit={wUnit} waterUnit={waterUnit} rpeLabel={rpeLabel} activeColumns={activeColumns} t={t} />
      </tr>
    </thead>
  );
};

TailHeadGroup.propTypes = { activeColumns: activeColumnsPropType };
TailHeadCells.propTypes = {
  wUnit: PropTypes.string,
  waterUnit: PropTypes.string,
  rpeLabel: PropTypes.string,
  activeColumns: activeColumnsPropType,
  t: PropTypes.func.isRequired,
};

DefaultHead.propTypes = {
  showMacroAdh: PropTypes.bool,
  wUnit: PropTypes.string,
  waterUnit: PropTypes.string,
  rpeLabel: PropTypes.string,
  activeColumns: activeColumnsPropType,
};
DetailedGroup.propTypes = { title: PropTypes.string };
DetailedHead.propTypes = {
  wUnit: PropTypes.string,
  waterUnit: PropTypes.string,
  rpeLabel: PropTypes.string,
  activeColumns: activeColumnsPropType,
  adherenceAsPercent: PropTypes.bool,
};
